import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, hasSuperAdminAccess } from '../../contexts/AuthContext';
import { securityLogService } from '../../api/services/SecurityLogService';
import type {
  SecurityAccessLogListItemViewModel,
  SecurityAccessLogQueryFilter,
} from '../../api/types/securityLog';

const DEFAULT_PAGE_SIZE = 50;

function formatOccurredAt(isoUtc: string): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return isoUtc;
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function localInputToIsoUtc(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function isoUtcToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FilterFormState {
  ipAddress: string;
  userId: string;
  from: string;
  to: string;
}

function readFormFromParams(params: URLSearchParams): FilterFormState {
  return {
    ipAddress: params.get('ipAddress') ?? '',
    userId: params.get('userId') ?? '',
    from: isoUtcToLocalInput(params.get('fromUtc')),
    to: isoUtcToLocalInput(params.get('toUtc')),
  };
}

function readFilterFromParams(params: URLSearchParams): SecurityAccessLogQueryFilter {
  const filter: SecurityAccessLogQueryFilter = {};
  const ip = params.get('ipAddress');
  const userId = params.get('userId');
  const fromUtc = params.get('fromUtc');
  const toUtc = params.get('toUtc');
  const pageNumber = Number(params.get('page') ?? '1');
  const pageSize = Number(params.get('pageSize') ?? String(DEFAULT_PAGE_SIZE));

  if (ip) filter.ipAddress = ip;
  if (userId) filter.userId = userId;
  if (fromUtc) filter.fromUtc = fromUtc;
  if (toUtc) filter.toUtc = toUtc;
  if (!Number.isNaN(pageNumber) && pageNumber > 0) filter.pageNumber = pageNumber;
  if (!Number.isNaN(pageSize) && pageSize > 0) filter.pageSize = pageSize;
  return filter;
}

const SecurityLogPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState<FilterFormState>(() => readFormFromParams(searchParams));
  const [rows, setRows] = useState<SecurityAccessLogListItemViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageNumber = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE));
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const activeFilter = useMemo(() => readFilterFromParams(searchParams), [searchParams]);

  const load = useCallback(async () => {
    if (!hasSuperAdminAccess(user)) return;
    setLoading(true);
    setError(null);
    try {
      const res = await securityLogService.query(activeFilter);
      if (res.isSuccess && res.data) {
        setRows(res.data.items ?? []);
        setTotalCount(res.data.totalCount ?? 0);
      } else {
        setRows([]);
        setTotalCount(0);
        setError(res.message ?? 'Güvenlik logları yüklenemedi.');
      }
    } catch {
      setRows([]);
      setTotalCount(0);
      setError('Güvenlik logları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (form.ipAddress.trim()) next.set('ipAddress', form.ipAddress.trim());
    if (form.userId.trim()) next.set('userId', form.userId.trim());
    const fromUtc = localInputToIsoUtc(form.from);
    const toUtc = localInputToIsoUtc(form.to);
    if (fromUtc) next.set('fromUtc', fromUtc);
    if (toUtc) next.set('toUtc', toUtc);
    next.set('page', '1');
    next.set('pageSize', String(pageSize));
    setSearchParams(next);
  };

  const goToPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
  };

  if (!hasSuperAdminAccess(user)) {
    return (
      <div className="alert alert-warning">
        Bu sayfa yalnızca süper yöneticiler için kullanılabilir.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4 mb-0">Güvenlik Logları</h1>
        <Link to="/admin-panel" className="btn btn-outline-secondary btn-sm">
          <i className="fas fa-arrow-left me-1" /> Panele dön
        </Link>
      </div>

      <form className="card card-body mb-3" onSubmit={applyFilters}>
        <div className="row g-2">
          <div className="col-md-3">
            <label className="form-label small" htmlFor="sec-log-ip">IP adresi</label>
            <input
              id="sec-log-ip"
              className="form-control form-control-sm"
              value={form.ipAddress}
              onChange={(e) => setForm((f) => ({ ...f, ipAddress: e.target.value }))}
              placeholder="örn. 203.0.113.10"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small" htmlFor="sec-log-user">Kullanıcı ID</label>
            <input
              id="sec-log-user"
              className="form-control form-control-sm"
              value={form.userId}
              onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              placeholder="GUID"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small" htmlFor="sec-log-from">Başlangıç</label>
            <input
              id="sec-log-from"
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.from}
              onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small" htmlFor="sec-log-to">Bitiş</label>
            <input
              id="sec-log-to"
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.to}
              onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
            />
          </div>
        </div>
        <div className="mt-2">
          <button type="submit" className="btn btn-primary btn-sm me-2" disabled={loading}>
            Filtrele
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setForm({ ipAddress: '', userId: '', from: '', to: '' });
              setSearchParams(new URLSearchParams({ page: '1', pageSize: String(pageSize) }));
            }}
          >
            Temizle
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-striped align-middle">
          <thead>
            <tr>
              <th>Zaman (UTC kayıt)</th>
              <th>Kullanıcı</th>
              <th>IP</th>
              <th>Kaynak</th>
              <th>Olay</th>
              <th>Metot</th>
              <th>Yol</th>
              <th>Durum</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">Yükleniyor…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">Kayıt bulunamadı.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${row.source}-${row.id}`}>
                  <td className="small text-nowrap">{formatOccurredAt(row.createdAt)}</td>
                  <td className="small font-monospace">{row.userId ?? '—'}</td>
                  <td className="small">{row.ipAddress ?? '—'}</td>
                  <td className="small">{row.source}</td>
                  <td className="small">{row.eventType}</td>
                  <td className="small">{row.httpMethod}</td>
                  <td className="small text-break">{row.path}</td>
                  <td className="small">{row.statusCode ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="small text-muted">
          Toplam {totalCount} kayıt · Sayfa {pageNumber} / {totalPages}
        </span>
        <div className="btn-group btn-group-sm">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={pageNumber <= 1 || loading}
            onClick={() => goToPage(pageNumber - 1)}
          >
            Önceki
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={pageNumber >= totalPages || loading}
            onClick={() => goToPage(pageNumber + 1)}
          >
            Sonraki
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityLogPage;
